<template>
    <div class="picker">
        <div v-if="modelValue" class="picker__selected">
            <span>
                <strong>{{ modelValue.name }}</strong>
                <span class="text-secondary ms-1">#{{ modelValue.id }}</span>
            </span>
            <button type="button" class="picker__clear" @click="limpiar" aria-label="Quitar selección">
                <i class="fas fa-times"></i>
            </button>
        </div>

        <template v-else>
            <input
                v-model="q"
                type="text"
                class="form-control form-control-sm"
                :placeholder="placeholder"
                @input="buscarConDebounce"
            />
            <div class="picker__results">
                <p v-if="loading" class="picker__hint"><i class="fas fa-spinner fa-spin me-1"></i> Buscando…</p>
                <p v-else-if="requiereTexto && q.trim().length < minChars" class="picker__hint">
                    Escribe al menos {{ minChars }} letras o el id
                </p>
                <p v-else-if="!resultados.length" class="picker__hint">Sin resultados</p>
                <template v-else>
                    <button
                        v-for="item in resultados"
                        :key="item.id"
                        type="button"
                        class="picker__item"
                        @click="seleccionar(item)"
                    >
                        <span>{{ item.name }}</span>
                        <span class="picker__id">#{{ item.id }}</span>
                    </button>
                </template>
            </div>
        </template>
    </div>
</template>

<script>
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";

// Buscador de localidades de un nivel. Con parentId lista los hijos de ese
// padre de entrada; sin parentId exige texto (ciudades tiene ~149.000 filas).
export default {
    name: "LocalidadPicker",
    props: {
        nivel: { type: String, required: true },
        parentId: { type: [Number, String], default: null },
        excludeId: { type: [Number, String], default: null },
        modelValue: { type: Object, default: null },
        placeholder: { type: String, default: "Buscar por nombre o id" },
    },
    emits: ["update:modelValue"],
    setup(props, { emit }) {
        const store = useStore();
        const q = ref("");
        const resultados = ref([]);
        const loading = ref(false);
        const minChars = 2;

        const requiereTexto = computed(() => !props.parentId);

        let timer = null;
        let ultimaPeticion = 0;

        const buscar = async () => {
            const texto = q.value.trim();
            if (requiereTexto.value && texto.length < minChars) {
                resultados.value = [];
                return;
            }
            const params = { per_page: 20 };
            if (texto) params.q = texto;
            if (props.parentId) params.parent_id = props.parentId;

            const peticion = ++ultimaPeticion;
            loading.value = true;
            try {
                const data = await store.dispatch("appLocalidades/listarAction", {
                    nivel: props.nivel,
                    params,
                });
                // Una respuesta vieja no debe pisar a una busqueda mas reciente
                if (peticion !== ultimaPeticion) return;
                resultados.value = (data.items?.data || []).filter(
                    (i) => String(i.id) !== String(props.excludeId)
                );
            } catch (e) {
                if (peticion === ultimaPeticion) resultados.value = [];
            } finally {
                if (peticion === ultimaPeticion) loading.value = false;
            }
        };

        const buscarConDebounce = () => {
            if (timer) clearTimeout(timer);
            timer = setTimeout(buscar, 350);
        };

        const seleccionar = (item) => emit("update:modelValue", item);

        const limpiar = () => {
            emit("update:modelValue", null);
            buscar();
        };

        watch(() => [props.nivel, props.parentId], () => {
            q.value = "";
            buscar();
        });

        onMounted(buscar);

        return { q, resultados, loading, minChars, requiereTexto, buscarConDebounce, seleccionar, limpiar };
    },
};
</script>

<style scoped>
.picker__results {
    margin-top: 6px;
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
}

.picker__hint {
    font-size: 0.76rem;
    color: #94A3B8;
    margin: 0;
    padding: 10px 12px;
}

.picker__item {
    display: flex;
    justify-content: space-between;
    width: 100%;
    background: none;
    border: none;
    border-bottom: 1px solid #F1F5F9;
    padding: 7px 12px;
    font-size: 0.8rem;
    color: #334155;
    text-align: left;
}

.picker__item:last-child { border-bottom: none; }
.picker__item:hover { background: #EFF6FF; }

.picker__id {
    color: #94A3B8;
    font-size: 0.72rem;
}

.picker__selected {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    border-radius: 8px;
    background: #EFF6FF;
    border: 1px solid #BFDBFE;
    font-size: 0.82rem;
    color: #1E3A8A;
}

.picker__clear {
    background: none;
    border: none;
    color: #64748B;
    padding: 0 4px;
}
</style>
